import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

// Antd
import {
  Badge,
  Button,
  Card,
  ConfigProvider,
  DatePicker,
  Dropdown,
  Empty,
  Input,
  Segmented,
  Space,
  Table,
} from "antd";

// Internal
import { UserType, useUrlParams } from "@/hooks/useUrlParams";
import {
  AttendanceStatus,
  AttendanceStatusTitles,
  IAttendance,
} from "@/types/attendance-sheet.types";
import {
  IPayment,
  PAYMENT_PROGRAM_CODES,
  PAYMENT_TYPE_CODES,
  PaymentProgramLabels,
  PaymentTypeLabels,
} from "@/types/payment.types";
import { parseEndDateQueryParam } from "@/utils/dates";
import { MIN_DATE_THRESHOLD } from "@/api/attendance";
import { getAttendanceTableColumns } from "./attendance-table.columns";
import { getActiveCertificatesColumns } from "./active-certificates.columns";
import { getPaymentsTableColumns } from "./payments-table.columns";
import { ChildCertificate, FamilyCertificate } from "@/api/__types/attendance";
import { FamilyCertificatesLegend } from "@/components/ChildActiveCertificates";
import { HomeView, getHomeView } from "./home-view";

// Icons
import { Filter, Search } from "@carbon/icons-react";

// MISC
import dayjs from "dayjs";

function getSelectedListParam(raw: string | null, allKeys: string[]): string[] {
  if (raw === null) {
    return allKeys;
  }

  return raw.split(",").filter(Boolean);
}

export default function EasTable({
  attendanceSheets,
  familyCertificates,
  payments,
  loading,
}: {
  attendanceSheets: IAttendance[];
  familyCertificates: FamilyCertificate[];
  payments: IPayment[];
  loading: boolean;
}) {
  // Search params
  const [searchParams, setSearchParams] = useSearchParams();
  const endQueryParam = searchParams.get("end") || "";
  const searchQuery = searchParams.get("search") || "";

  const { isFamilyView, userType } = useUrlParams({
    searchParams,
  });

  const homeView = getHomeView(searchParams, userType);
  const isPaymentsView = homeView === HomeView.PAYMENTS;
  const isCertificatesView = homeView === HomeView.ACTIVE_CERTIFICATES;
  const isAttendanceView = homeView === HomeView.ATTENDANCE_SHEETS;

  const numberOfActiveCertificates = useMemo(() => {
    return familyCertificates?.reduce(
      (accum, currentValue) => accum + currentValue.certificates.length,
      0,
    );
  }, [familyCertificates]);

  // Status
  const statusQueryParam = searchParams
    .get("status")
    ?.split(",")
    .filter(Boolean);

  const selectedStatuses = statusQueryParam
    ? statusQueryParam
    : isFamilyView
      ? [
          AttendanceStatus.PROVIDER_SUBMITTED,
          AttendanceStatus.FAMILY_DENIED,
          AttendanceStatus.NO_CARE_BILLING_PERIOD,
        ]
      : [AttendanceStatus.INCOMPLETE, AttendanceStatus.FAMILY_DENIED];

  const dataQuery = searchParams.get("data");

  const attendanceSheetColumns = getAttendanceTableColumns({
    dataQuery,
    endQueryParam,
    isFamilyView,
    userType,
  });
  const activeCertificatesColumns = getActiveCertificatesColumns();
  const paymentsTableColumns = getPaymentsTableColumns({ userType });

  const _numberOfRecords = Array.isArray(attendanceSheets)
    ? attendanceSheets.length
    : 0;

  const attendanceSheetsTableData = useMemo(() => {
    return (attendanceSheets || []).filter((datum) => {
      if (!selectedStatuses.includes(datum.status)) {
        return false;
      }

      const _childName = datum?.child?.name?.toLowerCase();
      const _childId = datum?.child?.id?.toString();

      if (userType === UserType.PROVIDER) {
        const _householdId = datum?.child?.householdId?.toString();
        return (
          _childName.includes(searchQuery.toLowerCase()) ||
          _childId.includes(searchQuery) ||
          _householdId.includes(searchQuery)
        );
      } else {
        const _providerId = datum?.provider?.id?.toString();
        return (
          _childName.includes(searchQuery.toLowerCase()) ||
          _childId.includes(searchQuery) ||
          _providerId.includes(searchQuery)
        );
      }
    });
  }, [attendanceSheets, searchQuery, statusQueryParam, userType]);

  const filteredFamilyCertificates = useMemo(() => {
    if (userType === UserType.PROVIDER) {
      return [];
    }

    return (familyCertificates || []).map(
      ({ certificates, ...familyCertificateRest }) => {
        return {
          ...familyCertificateRest,
          certificates:
            searchQuery && searchQuery !== ""
              ? certificates.filter(
                  (_certificate) =>
                    _certificate.providerName
                      ?.toLowerCase?.()
                      ?.includes(searchQuery.toLowerCase()) ||
                    _certificate.providerId.toString().includes(searchQuery) ||
                    _certificate.certificateId.toString().includes(searchQuery),
                )
              : certificates,
        };
      },
    );
  }, [
    attendanceSheets,
    familyCertificates,
    searchQuery,
    statusQueryParam,
    userType,
  ]);

  const programFilterItems = useMemo(() => {
    const labels = new Map<string, string>(
      PAYMENT_PROGRAM_CODES.map((code) => [code, PaymentProgramLabels[code]]),
    );

    for (const payment of payments || []) {
      if (payment.program) {
        labels.set(
          payment.program,
          payment.programDescription ||
            labels.get(payment.program) ||
            payment.program,
        );
      }
    }

    return Array.from(labels.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([key, label]) => ({ key, label }));
  }, [payments]);

  const paymentTypeFilterItems = useMemo(() => {
    const labels = new Map<string, string>(
      PAYMENT_TYPE_CODES.map((code) => [code, PaymentTypeLabels[code]]),
    );

    for (const payment of payments || []) {
      if (payment.paymentTypeCode) {
        labels.set(
          payment.paymentTypeCode,
          payment.paymentTypeDescription ||
            labels.get(payment.paymentTypeCode) ||
            payment.paymentTypeCode,
        );
      }
    }

    return Array.from(labels.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([key, label]) => ({ key, label }));
  }, [payments]);

  const programQueryParam = searchParams.get("program");
  const paymentTypeQueryParam = searchParams.get("paymentType");

  const selectedPrograms = useMemo(
    () =>
      getSelectedListParam(
        programQueryParam,
        programFilterItems.map((item) => item.key),
      ),
    [programFilterItems, programQueryParam],
  );
  const selectedPaymentTypes = useMemo(
    () =>
      getSelectedListParam(
        paymentTypeQueryParam,
        paymentTypeFilterItems.map((item) => item.key),
      ),
    [paymentTypeFilterItems, paymentTypeQueryParam],
  );

  const paymentsTableData = useMemo(() => {
    const query = searchQuery.toLowerCase();

    return (payments || []).filter((payment) => {
      if (
        payment.program &&
        selectedPrograms.length > 0 &&
        !selectedPrograms.includes(payment.program)
      ) {
        return false;
      }

      if (
        payment.paymentTypeCode &&
        selectedPaymentTypes.length > 0 &&
        !selectedPaymentTypes.includes(payment.paymentTypeCode)
      ) {
        return false;
      }

      if (!query) {
        return true;
      }

      const childName = payment.childName?.toLowerCase() || "";
      const childId = payment.childId?.toString() || "";
      const certificateId = payment.certificateId?.toString() || "";

      if (userType === UserType.PROVIDER) {
        const householdId = payment.householdId?.toString() || "";
        return (
          childName.includes(query) ||
          childId.includes(query) ||
          householdId.includes(query) ||
          certificateId.includes(query)
        );
      }

      const providerId = payment.providerId?.toString() || "";
      const providerName = payment.providerName?.toLowerCase() || "";
      return (
        childName.includes(query) ||
        childId.includes(query) ||
        providerId.includes(query) ||
        certificateId.includes(query) ||
        providerName.includes(query)
      );
    });
  }, [payments, searchQuery, selectedPaymentTypes, selectedPrograms, userType]);

  const _hasRecordsOutsideOfFilteredState =
    attendanceSheetsTableData?.length !== _numberOfRecords;

  const _numberOfPayments = Array.isArray(payments) ? payments.length : 0;
  const _hasPaymentsOutsideOfFilteredState =
    paymentsTableData.length !== _numberOfPayments;

  const tabOptions =
    userType === UserType.FAMILY
      ? [
          {
            label: "Attendance Sheets",
            value: HomeView.ATTENDANCE_SHEETS,
          },
          {
            label: "Active Certificates",
            value: HomeView.ACTIVE_CERTIFICATES,
          },
          {
            label: "Payments",
            value: HomeView.PAYMENTS,
          },
        ]
      : [
          {
            label: "Attendance Sheets",
            value: HomeView.ATTENDANCE_SHEETS,
          },
          {
            label: "Payments",
            value: HomeView.PAYMENTS,
          },
        ];

  const cardTitle = loading
    ? undefined
    : isPaymentsView
      ? `${paymentsTableData.length}${
          _hasPaymentsOutsideOfFilteredState ? ` of ${_numberOfPayments}` : ""
        } Payment${_numberOfPayments !== 1 ? "s" : ""}`
      : isAttendanceView
        ? `${attendanceSheetsTableData.length}${
            _hasRecordsOutsideOfFilteredState ? ` of ${_numberOfRecords}` : ""
          } Attendance Sheet${_numberOfRecords !== 1 ? "s" : ""}`
        : `${numberOfActiveCertificates} Active Certificate${
            numberOfActiveCertificates !== 1 ? "s" : ""
          }`;

  const updateSearchParams = (updates: Record<string, string>) => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...updates,
    });
  };

  return (
    <>
      <Segmented
        options={tabOptions}
        value={homeView}
        size="large"
        className="mb-6 border border-solid border-gray-300"
        onChange={(value) => {
          setSearchParams({
            ...Object.fromEntries(searchParams.entries()),
            view: value,
            ...(userType === UserType.FAMILY ? { familyView: value } : {}),
            // clear search query
            search: "",
          });
        }}
      />

      <Card
        title={cardTitle}
        extra={
          <Space className="flex-wrap">
            {isPaymentsView && (
              <>
                <Dropdown
                  menu={{
                    items: programFilterItems,
                    selectable: true,
                    selectedKeys: selectedPrograms,
                    multiple: true,
                    onSelect: ({ selectedKeys }) => {
                      updateSearchParams({
                        program: selectedKeys.join(","),
                      });
                    },
                    onDeselect: ({ selectedKeys }) => {
                      updateSearchParams({
                        program: selectedKeys.join(","),
                      });
                    },
                  }}
                  trigger={["click"]}
                >
                  <Button icon={<Filter />}>
                    Program{" "}
                    <Badge
                      color="blue"
                      count={selectedPrograms.length}
                      className="ml-2"
                    />
                  </Button>
                </Dropdown>

                <Dropdown
                  menu={{
                    items: paymentTypeFilterItems,
                    selectable: true,
                    selectedKeys: selectedPaymentTypes,
                    multiple: true,
                    onSelect: ({ selectedKeys }) => {
                      updateSearchParams({
                        paymentType: selectedKeys.join(","),
                      });
                    },
                    onDeselect: ({ selectedKeys }) => {
                      updateSearchParams({
                        paymentType: selectedKeys.join(","),
                      });
                    },
                  }}
                  trigger={["click"]}
                >
                  <Button icon={<Filter />}>
                    Payment Type{" "}
                    <Badge
                      color="blue"
                      count={selectedPaymentTypes.length}
                      className="ml-2"
                    />
                  </Button>
                </Dropdown>
              </>
            )}

            <DatePicker
              allowClear={false}
              picker="month"
              format="MMM YYYY"
              disabledDate={(current) => {
                // Disable dates before the MIN_DATE_THRESHOLD
                if (current.isBefore(dayjs(MIN_DATE_THRESHOLD))) {
                  return true;
                }

                // Attendance sheets cannot be opened for the current month
                // before the 25th. Payments can be released any day.
                if (!isPaymentsView && current.isSame(dayjs(), "month")) {
                  return dayjs().date() < 25;
                }

                return current > dayjs().endOf("month");
              }}
              onChange={(_date) => {
                updateSearchParams({
                  end: _date.format("YYYY-MM"),
                });
              }}
              value={parseEndDateQueryParam(endQueryParam)}
              style={{
                width: "109px",
              }}
            />

            {isAttendanceView && (
              <Dropdown
                menu={{
                  items: Object.keys(
                    AttendanceStatusTitles[userType] || {},
                  ).map((_status) => {
                    return {
                      label:
                        AttendanceStatusTitles[userType][
                          _status as AttendanceStatus
                        ] || _status,
                      key: _status,
                    };
                  }),
                  selectable: true,
                  selectedKeys: selectedStatuses,
                  multiple: true,
                  onSelect: ({ selectedKeys }) => {
                    updateSearchParams({
                      status: selectedKeys.join(","),
                    });
                  },
                  onDeselect: ({ selectedKeys }) => {
                    updateSearchParams({
                      status: selectedKeys.join(","),
                    });
                  },
                }}
                trigger={["click"]}
              >
                <Button icon={<Filter />}>
                  Filter by Status{" "}
                  <Badge
                    color="blue"
                    count={selectedStatuses.length}
                    className="ml-2"
                  />
                </Button>
              </Dropdown>
            )}

            <Input
              allowClear
              placeholder="Search by name or ID"
              prefix={<Search />}
              onChange={(e) => {
                updateSearchParams({
                  search: e.target.value,
                });
              }}
              value={searchQuery}
            />
          </Space>
        }
        styles={{
          body: {
            padding: "0px",
          },
        }}
      >
        {isAttendanceView && (
          <ConfigProvider
            renderEmpty={() => (
              <div style={{ textAlign: "center" }}>
                <Empty
                  className="my-12"
                  description={
                    _hasRecordsOutsideOfFilteredState
                      ? "There is data outside of your current filters"
                      : "No data"
                  }
                >
                  {_hasRecordsOutsideOfFilteredState && (
                    <Button
                      className="m-auto"
                      danger
                      onClick={() => {
                        updateSearchParams({
                          status: Object.keys(
                            AttendanceStatusTitles[userType] || {},
                          ).join(","),
                          search: "",
                        });
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                </Empty>
              </div>
            )}
          >
            <Table
              columns={attendanceSheetColumns}
              dataSource={attendanceSheetsTableData}
              pagination={{
                hideOnSinglePage: true,
                style: { marginRight: "24px" },
              }}
              rowKey="id"
              scroll={{ x: "max-content" }}
            />
          </ConfigProvider>
        )}

        {isPaymentsView && (
          <ConfigProvider
            renderEmpty={() => (
              <div style={{ textAlign: "center" }}>
                <Empty
                  className="my-12"
                  description={
                    _hasPaymentsOutsideOfFilteredState
                      ? "There is data outside of your current filters"
                      : "No data"
                  }
                >
                  {_hasPaymentsOutsideOfFilteredState && (
                    <Button
                      className="m-auto"
                      danger
                      onClick={() => {
                        updateSearchParams({
                          program: programFilterItems
                            .map((item) => item.key)
                            .join(","),
                          paymentType: paymentTypeFilterItems
                            .map((item) => item.key)
                            .join(","),
                          search: "",
                        });
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                </Empty>
              </div>
            )}
          >
            <Table<IPayment>
              columns={paymentsTableColumns}
              dataSource={paymentsTableData}
              pagination={{
                hideOnSinglePage: true,
                style: { marginRight: "24px" },
              }}
              rowKey="transactionDetailId"
              scroll={{ x: "max-content" }}
            />
          </ConfigProvider>
        )}
      </Card>

      {!loading && isCertificatesView && (
        <div className="mt-6 flex flex-col gap-6">
          {filteredFamilyCertificates?.map(
            ({ childId, childName, certificates }) => {
              return (
                <Card
                  key={childId}
                  title={
                    <>
                      {childName} (ID: {childId})
                    </>
                  }
                  styles={{
                    body: {
                      padding: "0px",
                    },
                  }}
                >
                  <Table<ChildCertificate>
                    columns={activeCertificatesColumns}
                    dataSource={certificates}
                    pagination={{
                      hideOnSinglePage: true,
                      style: { marginRight: "24px" },
                    }}
                    rowKey="id"
                    rowClassName={(record) => {
                      if (record.expiringSoonCritical) {
                        return "status-critical";
                      }

                      return "";
                    }}
                    scroll={{ x: "max-content" }}
                  />
                </Card>
              );
            },
          )}

          <FamilyCertificatesLegend />
        </div>
      )}
    </>
  );
}
